<template>
  <div class="w-screen min-h-screen overflow-y-auto overflow-x-hidden bg-white relative pb-20">
    <div class="w-full min-h-screen overflow-y-auto overflow-x-hidden bg-white relative">
      <!-- Alerts -->
      <div class="z-10 fixed left-0 w-full px-3">
        <div v-if="alertSuccess" class="w-full rounded-lg p-4 flex justify-between bg-green-300 text-white mt-2">
          <span>{{ alertMessage }}</span>
          <svg @click="alertSuccess = false" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 cursor-pointer"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <div v-if="alertFailed" class="w-full rounded-lg p-4 flex justify-between bg-red-300 text-white mt-2">
          <span>{{ alertMessage }}</span>
          <svg @click="alertFailed = false" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 cursor-pointer"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      </div>

      <!-- Header -->
      <div class="w-full bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center">
        <div class="font-bold text-blue-600">E-TRANSAKSI</div>
        <svg @click="logout" xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 stroke-current text-red-400 cursor-pointer" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </div>

      <!-- Content -->
      <div class="p-4">
        <!-- Title -->
        <div class="text-center mb-4">
          <h5 class="font-bold text-blue-600">LAPORAN KEUANGAN</h5>
          <h6 class="font-bold text-red-500">EF-PRINT</h6>
        </div>

        <div class="mb-8 p-4 border rounded-lg bg-gray-50 shadow-sm">
          <!-- Filter Date -->
          <div class="mb-4 flex justify-center items-center">
            <label class="mr-2 font-semibold text-sm">Filter Tgl:</label>
            <input v-model="filterDate" type="date" class="border py-1 px-2 rounded-md text-sm"
              @change="getDataKeuangan">
          </div>

          <!-- Summary Boxes -->
          <div class="grid grid-cols-2 gap-2 mb-4 sm:grid-cols-4">
            <div class="bg-green-200 text-green-800 p-3 rounded-lg border border-green-300">
              <div class="text-xs font-semibold">Pemasukan :</div>
              <div class="text-sm font-bold">Rp. {{ formatRupiah(totalPemasukan) }}</div>
            </div>
            <div class="bg-red-200 text-red-800 p-3 rounded-lg border border-red-300">
              <div class="text-xs font-semibold">Pengeluaran :</div>
              <div class="text-sm font-bold">Rp. {{ formatRupiah(totalPengeluaran) }}</div>
            </div>
            <div class="bg-blue-200 text-blue-800 p-3 rounded-lg border border-blue-300">
              <div class="text-xs font-semibold">Saldo DI KAS :</div>
              <div class="text-sm font-bold">Rp. {{ formatRupiah(saldoKas) }}</div>
            </div>
            <div class="bg-purple-200 text-purple-800 p-3 rounded-lg border border-purple-300">
              <div class="text-xs font-semibold">Saldo DIBANK :</div>
              <div class="text-sm font-bold">Rp. {{ formatRupiah(saldoBank) }}</div>
            </div>
          </div>

          <!-- Form Section -->
          <hr class="my-4 border-gray-300">
          <h4 class="font-bold text-sm mb-3 text-gray-700">INPUT TRANSAKSI</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <!-- Date -->
            <div>
              <label class="block font-semibold mb-1 text-xs">Tanggal & Waktu :</label>
              <input v-model="form.tanggal" type="datetime-local" class="w-full border py-1 px-2 rounded-md text-sm">
            </div>

            <!-- Type & Location Selection -->
            <div>
              <label class="block font-semibold mb-1 text-xs">Jenis & Lokasi :</label>
              <div class="flex flex-wrap gap-2">
                <button type="button" @click="form.jenis = form.jenis === 'debet' ? 'kredit' : 'debet'"
                  class="py-1 px-2 rounded-md border-2 font-semibold text-xs flex-1"
                  :class="form.jenis === 'debet' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-red-100 border-red-500 text-red-700'">
                  {{ form.jenis === 'debet' ? 'Masuk' : 'Keluar' }}
                </button>
                <button type="button" @click="form.lokasi = 'kas'"
                  class="py-1 px-2 rounded-md border-2 font-semibold text-xs flex-1"
                  :class="form.lokasi === 'kas' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-gray-100 border-gray-300'">
                  Kas Di Tangan
                </button>
                <button type="button" @click="form.lokasi = 'bank'"
                  class="py-1 px-2 rounded-md border-2 font-semibold text-xs flex-1"
                  :class="form.lokasi === 'bank' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-gray-100 border-gray-300'">
                  Kas Di Bank
                </button>
              </div>
            </div>

            <!-- Jumlah -->
            <div>
              <label class="block font-semibold mb-1 text-xs">Jumlah (Rp) :</label>
              <input v-model="form.jumlah" type="number" class="w-full border py-1 px-2 rounded-md text-sm"
                placeholder="0" @input="handleInputAmount">
            </div>

            <!-- Keterangan -->
            <div>
              <label class="block font-semibold mb-1 text-xs">Keterangan / Uraian :</label>
              <input v-model="form.keterangan" type="text" class="w-full border py-1 px-2 rounded-md text-sm"
                placeholder="Masukkan keterangan">
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-2">
            <button v-if="isEdit" @click="batalEdit"
              class="bg-gray-500 hover:bg-gray-600 text-white py-1 px-4 rounded-md font-semibold text-xs">
              Batal
            </button>
            <button @click="tambahTransaksi"
              class="bg-blue-600 hover:bg-blue-700 text-white py-1 px-4 rounded-md font-semibold text-xs flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {{ isEdit ? 'Update' : 'Simpan' }}
            </button>
          </div>
        </div>

        <!-- Data Tables -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Table Kas -->
          <div>
            <div class="flex justify-between items-center mb-3">
              <h3 class="font-bold text-sm text-blue-800">DATA KAS</h3>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full border-collapse border border-gray-400 text-xs">
                <thead>
                  <tr class="bg-blue-100">
                    <th class="border border-gray-400 px-1 py-1">TGL</th>
                    <th class="border border-gray-400 px-1 py-1">KETERANGAN</th>
                    <th class="border border-gray-400 px-1 py-1">MASUK</th>
                    <th class="border border-gray-400 px-1 py-1">KELUAR</th>
                    <th class="border border-gray-400 px-1 py-1">AKSI</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="dataKas.length < 1">
                    <td colspan="5" class="border border-gray-400 px-1 py-2 text-center text-gray-500">
                      Belum ada data kas
                    </td>
                  </tr>
                  <tr v-for="(item, index) in dataKas" :key="'kas-' + index">
                    <td class="border border-gray-400 px-1 py-1 text-center">{{ item.tgl }}</td>
                    <td class="border border-gray-400 px-1 py-1">{{ item.keterangan }}</td>
                    <td class="border border-gray-400 px-1 py-1 text-right text-green-600">{{ item.pemasukan ?
                      formatRupiah(item.pemasukan) : '' }}</td>
                    <td class="border border-gray-400 px-1 py-1 text-right text-red-600">{{ item.pengeluaran ?
                      formatRupiah(item.pengeluaran) : '' }}</td>
                    <td class="border border-gray-400 px-1 py-1 text-center whitespace-nowrap">
                      <button @click="editData(item)" class="text-blue-600 hover:text-blue-800 mr-1"
                        title="Edit">✏️</button>
                      <button @click="hapusData(item.id)" class="text-red-600 hover:text-red-800"
                        title="Hapus">🗑️</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Table Bank -->
          <div>
            <div class="flex justify-between items-center mb-3">
              <h3 class="font-bold text-sm text-purple-800">DATA BANK</h3>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full border-collapse border border-gray-400 text-xs">
                <thead>
                  <tr class="bg-purple-100">
                    <th class="border border-gray-400 px-1 py-1">TGL</th>
                    <th class="border border-gray-400 px-1 py-1">KETERANGAN</th>
                    <th class="border border-gray-400 px-1 py-1">MASUK</th>
                    <th class="border border-gray-400 px-1 py-1">KELUAR</th>
                    <th class="border border-gray-400 px-1 py-1">AKSI</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="dataBank.length < 1">
                    <td colspan="5" class="border border-gray-400 px-1 py-2 text-center text-gray-500">
                      Belum ada data bank
                    </td>
                  </tr>
                  <tr v-for="(item, index) in dataBank" :key="'bank-' + index">
                    <td class="border border-gray-400 px-1 py-1 text-center">{{ item.tgl }}</td>
                    <td class="border border-gray-400 px-1 py-1">{{ item.keterangan }}</td>
                    <td class="border border-gray-400 px-1 py-1 text-right text-green-600">{{ item.pemasukan ?
                      formatRupiah(item.pemasukan) : '' }}</td>
                    <td class="border border-gray-400 px-1 py-1 text-right text-red-600">{{ item.pengeluaran ?
                      formatRupiah(item.pengeluaran) : '' }}</td>
                    <td class="border border-gray-400 px-1 py-1 text-center whitespace-nowrap">
                      <button @click="editData(item)" class="text-blue-600 hover:text-blue-800 mr-1"
                        title="Edit">✏️</button>
                      <button @click="hapusData(item.id)" class="text-red-600 hover:text-red-800"
                        title="Hapus">🗑️</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <bottom-nav></bottom-nav>
    </div>
  </div>
</template>

<script>
import BottomNav from '../components/BottomNav.vue'

export default {
  name: 'Home',
  components: { BottomNav },
  data () {
    return {
      alertSuccess: false,
      alertFailed: false,
      alertMessage: '',
      form: {
        tanggal: this.getLocalDateISOString().slice(0, 16),
        jenis: 'debet',
        lokasi: 'kas',

        keterangan: '',
        jumlah: 0
      },
      totalPemasukan: 0,
      totalPengeluaran: 0,
      saldoKas: 0,
      saldoBank: 0,
      dataKeuangan: [],
      isEdit: false,
      editId: null,
      filterDate: this.getLocalDateISOString().slice(0, 10)
    }
  },
  computed: {
    token () {
      return this.$store.state.token
    },
    dataKas () {
      return this.dataKeuangan.filter(item => !item.lokasi || item.lokasi.toLowerCase() === 'kas')
    },
    dataBank () {
      return this.dataKeuangan.filter(item => item.lokasi && item.lokasi.toLowerCase() === 'bank')
    }
  },
  created () {
    this.getDataKeuangan()
  },
  methods: {
    formatRupiah (angka) {
      if (!angka) return '0'
      return new Intl.NumberFormat('id-ID').format(angka)
    },
    handleInputAmount (e) {
      const val = e.target.value
      if (val.length > 1 && val.startsWith('0') && val[1] !== '.') {
        const newVal = parseInt(val)
        this.form.jumlah = newVal
        e.target.value = newVal
      }
    },
    getDataKeuangan () {
      this.axios.get('e_transaksi/transaksi/get_all?token=' + this.token + '&date=' + this.filterDate).then(response => {
        if (response.data.status) {
          this.dataKeuangan = response.data.data
          this.totalPemasukan = response.data.totalPemasukan
          this.totalPengeluaran = response.data.totalPengeluaran
          this.saldoKas = response.data.saldoKas
          this.saldoBank = response.data.saldoBank
        }
      }).catch(error => {
        console.error('Error fetching data:', error)
      })
    },
    tambahTransaksi () {
      const formData = new FormData()
      formData.append('token', this.token)
      formData.append('tanggal', this.form.tanggal)
      formData.append('jenis', this.form.jenis)
      formData.append('lokasi', this.form.lokasi)
      formData.append('keterangan', this.form.keterangan)
      formData.append('jumlah', this.form.jumlah)

      let url = 'e_transaksi/transaksi/store'
      if (this.isEdit) {
        url = 'e_transaksi/transaksi/update/' + this.editId
      }

      this.axios.post(url, formData).then(response => {
        if (response.data.status) {
          this.alertMessage = response.data.message
          this.alertSuccess = true
          // Reset form
          this.form.keterangan = ''
          this.form.jumlah = 0
          this.form.jumlah = 0
          this.form.tanggal = this.getLocalDateISOString().slice(0, 16)
          this.isEdit = false
          this.editId = null
          // Refresh data
          this.getDataKeuangan()
        } else {
          this.alertMessage = response.data.message
          this.alertFailed = true
        }
        setTimeout(() => {
          this.alertSuccess = false
          this.alertFailed = false
        }, 3000)
      }).catch(error => {
        console.error('Error adding transaction:', error)
        this.alertMessage = 'Gagal menyimpan transaksi'
        this.alertFailed = true
        setTimeout(() => {
          this.alertFailed = false
        }, 3000)
      })
    },
    editData (item) {
      this.isEdit = true
      this.editId = item.id
      let dateValue = new Date()
      if (item.tgl) {
        // Format item.tgl: dd/mm/yyyy hh:mm
        const [datePart, timePart] = item.tgl.split(' ')
        const [day, month, year] = datePart.split('/')
        dateValue = new Date(`${year}-${month}-${day}T${timePart}`)
        // Adjust for timezone offset to keep the time literal
        const offset = dateValue.getTimezoneOffset()
        dateValue = new Date(dateValue.getTime() - (offset * 60 * 1000))
      }
      this.form.tanggal = dateValue.toISOString().slice(0, 16)
      this.form.keterangan = item.keterangan
      this.form.jenis = item.pemasukan ? 'debet' : 'kredit'
      this.form.jumlah = item.pemasukan ? item.pemasukan : item.pengeluaran
      this.form.lokasi = item.lokasi ? item.lokasi : 'kas'
    },
    batalEdit () {
      this.isEdit = false
      this.editId = null
      this.form.keterangan = ''
      this.form.jumlah = 0

      this.form.tanggal = this.getLocalDateISOString().slice(0, 16)
    },
    getLocalDateISOString () {
      const now = new Date()
      const offset = now.getTimezoneOffset()
      const localDate = new Date(now.getTime() - (offset * 60 * 1000))
      return localDate.toISOString()
    },
    hapusData (id) {
      if (!confirm('Anda yakin ingin menghapus data ini?')) return

      this.axios.delete('e_transaksi/transaksi/delete/' + id + '?token=' + this.token).then(response => {
        if (response.data.status) {
          this.alertMessage = 'Data berhasil dihapus'
          this.alertSuccess = true
          this.getDataKeuangan()
        } else {
          this.alertMessage = response.data.message
          this.alertFailed = true
        }
        setTimeout(() => {
          this.alertSuccess = false
          this.alertFailed = false
        }, 3000)
      }).catch(error => {
        console.error('Error deleting:', error)
        this.alertMessage = 'Gagal menghapus data'
        this.alertFailed = true
        setTimeout(() => {
          this.alertFailed = false
        }, 3000)
      })
    },
    logout () {
      localStorage.removeItem('X-FPRINT-TOKEN')
      localStorage.removeItem('X-FPRINT-USER')
      this.$store.dispatch('set_token', null)
      this.$store.dispatch('set_user', null)
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>
